import React, { useEffect, useRef, useState } from "react";
import { getAuth, signOut } from "firebase/auth";
import "./Login.css";
import "./AccountChoice.css";
import StudyDenLogo from "./assets/images/branding/Studyden_Primary.svg";

// Enough of the address to tell two accounts apart without displaying it in
// full on a shared screen: "jane.doe@school.org" -> "ja•••@school.org".
export const maskEmail = (email) => {
  if (!email || !email.includes("@")) return "";
  const [local, domain] = email.split("@");
  const visible = local.length > 2 ? local.slice(0, 2) : local.slice(0, 1);
  return `${visible}•••@${domain}`;
};

// Shown on /login and /signup when the browser already holds a session that was
// not created on this page (for example, restored from a previous visit on a
// shared computer). Renders only the account's name and masked email; nothing
// from the account's dashboard is mounted until the person chooses to continue.
const AccountChoice = ({ account, onContinue }) => {
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState("");
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const name = account.displayName || account.username || "this account";
  const maskedEmail = maskEmail(account.email);

  const handleUseAnotherAccount = async () => {
    setSwitching(true);
    setError("");
    try {
      // App's auth listener tears down the old profile listener, clears the
      // in-memory user, and this route then renders the sign-in form.
      await signOut(getAuth());
    } catch (err) {
      console.error("Error signing out to switch accounts:", err);
      if (mountedRef.current) {
        setError("We couldn't sign out of the existing account. Please try again.");
        setSwitching(false);
      }
    }
  };

  return (
    <div className="studyden-login sd-account-choice">
      <main className="sd-account-choice-panel" aria-labelledby="sd-account-choice-title" aria-busy={switching}>
        <img src={StudyDenLogo} alt="StudyDen — Learn. Assess. Progress." className="sd-account-choice-logo" />
        <header className="sd-form-header">
          <p className="sd-eyebrow">Already signed in</p>
          <h1 id="sd-account-choice-title">Choose an account</h1>
          <p>Someone is already signed in on this browser. Continue only if this is your account.</p>
        </header>

        <div className="sd-account-card" aria-label="Signed-in account">
          <span className="sd-account-avatar" aria-hidden="true">{name.charAt(0).toUpperCase()}</span>
          <span className="sd-account-identity">
            <strong>{name}</strong>
            {maskedEmail && <small>{maskedEmail}</small>}
          </span>
        </div>

        <div className="sd-account-actions">
          <button type="button" className="sd-primary-button" onClick={onContinue} disabled={switching}>
            Continue as {name}
          </button>
          <button type="button" className="sd-google-button" onClick={handleUseAnotherAccount} disabled={switching}>
            {switching ? "Signing out…" : "Use another account"}
          </button>
        </div>

        {error && <p className="sd-account-error" role="alert">{error}</p>}
      </main>
    </div>
  );
};

export default AccountChoice;
